// import node module libraries
import React, {Fragment, useEffect, useMemo, useState} from 'react';
import {Link} from 'react-router-dom';
import {Col, Row, Table} from 'react-bootstrap';
import {useFilters, useGlobalFilter, usePagination, useTable} from 'react-table';

// import custom components
import GlobalFilter from 'components/elements/advance-table/GlobalFilter';
import Pagination from 'components/elements/advance-table/Pagination';

// import utility file
import {Avatar} from '../../../elements/bootstrap/Avatar';
import TenueService from '../../services/TenueService';

const TenuesListItems = () => {
    const [originalTenues, setOriginalTenues] = useState([]);
    const [tenues, setTenues] = useState([]);

    useEffect(() => {
        Promise.all([TenueService.getAllTenues()]).then(([tenuesData]) => {
            setOriginalTenues(tenuesData);
            setTenues(tenuesData);
        });
    }, []);

    const formatValue = (value, columnId) => {
        if (typeof value === 'string') {
            if (columnId === 'type' || columnId === 'taille' && value) {
                return formatText(value).toUpperCase();
            } else if (columnId === 'prix') {
                return new Intl.NumberFormat('fr-FR', {style: 'currency', currency: 'XAF'}).format(value);
            } else if (columnId === 'fournisseur') {
                return value && value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
            } else {
                return value;
            }
        }
        return value;
    };

    const formatText = (value) => {
        return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    };

    const getInitials = (Tenue) => {
        const taille = Tenue.taille
            ? Tenue.taille.charAt(0).toUpperCase()
            : '';
        const type = Tenue.type ? Tenue.type.charAt(0).toUpperCase() : '';
        return `${type} ${taille}`;
    };

    const columns = useMemo(
        () => [
            {accessor: 'id', Header: 'ID', show: false},
            {
                accessor: 'type',
                Header: 'Type de tenue',
                Cell: ({value, row}) => {
                    const initials = getInitials(row.original);
                    return (
                        <div className="d-flex align-items-center">
                            <Avatar
                                type="initial"
                                size="md"
                                name={initials}
                                className="rounded-circle"
                                alt={initials}
                            />
                            {value ? (
                                <Link to={`/finances/tenues/${row.original.id}`}>
                                    <h5 className="mb-0 ms-2">{formatValue(value, 'type')}</h5>
                                </Link>
                            ) : null}
                        </div>
                    );
                }
            },

            {
                accessor: 'taille',
                Header: 'Taille',
                Cell: ({value}) => <div>{formatValue(value, 'taille')}</div>
            },
            {
                accessor: 'fournisseur',
                Header: 'Fournisseur',
                Cell: ({value}) => <div>{formatValue(value, 'fournisseur')}</div>
            },
            {
                accessor: 'quantite',
                Header: 'Quantité',
                Cell: ({value}) => <div>{formatValue(value, 'quantite')}</div>
            },
            {
                accessor: 'prix',
                Header: 'Prix',
                Cell: ({value}) => <div>{formatValue(value, 'prix')}</div>
            }
        ],
        []
    );

    const data = useMemo(() => {
        return tenues.map((tenue) => ({
            ...tenue
        }));
    }, [tenues]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        page,
        nextPage,
        previousPage,
        state,
        gotoPage,
        pageCount,
        prepareRow,
        setGlobalFilter
    } = useTable(
        {
            columns,
            data,
            initialState: {
                pageSize: 10,
                hiddenColumns: columns.map((column) => {
                    if (column.show === false) return column.accessor || column.id;
                    else return false;
                })
            }
        },
        useFilters,
        useGlobalFilter,
        usePagination
    );

    const {pageIndex, globalFilter} = state;

    return (
        <Fragment>
            <div className="overflow-hidden p-3">
                <Row className="justify-content-md-between mb-4 mb-xl-0 ">
                    <Col xxl={3} lg={4} md={6} xs={12}>
                        <GlobalFilter
                            filter={globalFilter}
                            setFilter={setGlobalFilter}
                            placeholder="Rechercher une tenue"
                        />
                    </Col>
                </Row>
            </div>

            <Table
                hover
                responsive
                {...getTableProps()}
                className="table text-nowrap table-centered"
            >
                <thead className="table-light">
                {headerGroups.map((headerGroup) => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map((column) => (
                            <th {...column.getHeaderProps()}>{column.render('Header')}</th>
                        ))}
                    </tr>
                ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                {page.map((row) => {
                    prepareRow(row);
                    return (
                        <tr {...row.getRowProps()}>
                            {row.cells.map((cell) => {
                                return (
                                    <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                                );
                            })}
                        </tr>
                    );
                })}
                </tbody>
            </Table>

            <Pagination
                previousPage={previousPage}
                pageCount={pageCount}
                pageIndex={pageIndex}
                gotoPage={gotoPage}
                nextPage={nextPage}
            />
        </Fragment>
    );
};

export default TenuesListItems;
